import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getProjects, updateProject, deleteProject} from '../../../services/projectService';
import './Projects.css';
import { toast } from "react-toastify";


const BACKEND_URL = process.env.REACT_APP_API_BASE_URL ;

function Projects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [errors, setErrors] = useState({}); // Stores validation errors
  const [saveLoading, setSaveLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
  }, [navigate]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getProjects();
      setProjects(data);
    } catch (err) {
      const errorMsg = err.message || 'Failed to fetch projects';
      setError(errorMsg);
      console.error('Error:', err);
      
      if (err.message.includes('Session expired') || err.message.includes('Authorization token not found')) {
        setTimeout(() => {
          navigate('/');
        }, 2000);
      }
    } finally {
      setLoading(false);
    }
  };

  // const getProjectLogo = (logoUrl) => {
  //   if (!logoUrl) return null;
  //   if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
  //     return logoUrl;
  //   }
  //   if (logoUrl.startsWith('/')) {
  //     return `${BACKEND_URL}${logoUrl}`;
  //   }
  //   return `${BACKEND_URL}/media/${logoUrl}`;
  // };

  const getProjectLogo = (logoUrl) => {
  if (!logoUrl) return null;

  // 1. If it's already an absolute external link, return it directly
  if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
    return logoUrl;
  }

  // 2. Clean up any leading slash to make path parsing predictable
  const cleanPath = logoUrl.startsWith('/') ? logoUrl.slice(1) : logoUrl;

  // 3. If the path already includes 'media/', don't double-add it
  if (cleanPath.startsWith('media/')) {
    return `${BACKEND_URL}/${cleanPath}`;
  }

  // 4. Fallback for raw 'project_logos/filename.png' paths
  return `${BACKEND_URL}/media/${cleanPath}`;
};

  // Open edit mode with all modifiable parameters
  const handleEditClick = () => {
    setEditMode(true);
    setErrors({});
    setEditData({
      name: selectedProject.name || '',
      base_url: selectedProject.base_url || '',
      logo_url: selectedProject.logo_url || '',
      sender_name: selectedProject.sender_name || '',
      sender_email: selectedProject.sender_email || '',
    });
  };

  // Physical key interceptor to block illegal characters instantly
  const handleKeyDown = (e, field) => {
    if (field === 'name') {
      // Disallow commas, semicolons, and special characters (!, $, %, ^, &, *, (, ))
      const illegalKeys = [',', ';', '!', '$', '%', '^', '&', '*', '(', ')','@','#', '/', '\\','<','>', '"', '`', '~','.','1','2','3','4','5','6','7','8','9','0'];
      if (illegalKeys.includes(e.key)) {
        e.preventDefault();
      }
    }
  };

  // Real-time character rule checking
  const validateField = (field, value) => {
    let errorMsg = '';

    if (!value.toString().trim()) {
      errorMsg = 'This field is required.';
    } else {
      switch (field) {
        case 'name':
          if (value.length < 2 || value.length > 50) {

            errorMsg = 'Name must be between 2 and 50 characters.';
          }
          // Fallback regex safety check if data is pasted in
          if (/[,;!$%^&*()]/.test(value)) {
            errorMsg = 'Contains illegal characters.';
          }
          break;
        case 'sender_name':
          if (value.length < 2 || value.length > 50) {
            errorMsg = 'Sender name must be between 2 and 50 characters.';
          }
          break;
        case 'sender_email':
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
          if (!emailRegex.test(value)) {
            errorMsg = 'Please enter a valid email address.';
          }
          break;
        case 'base_url':
const urlRegex = /^(http:\/\/|https:\/\/)/;
  if (!urlRegex.test(value)) {
    errorMsg = 'URL must start with http:// or https://';
  } else if (value.length > 200) { // <-- Added length limit check
    errorMsg = 'URL cannot exceed 200 characters.';
  }
          break;
        default:
          break;
      }
    }

    setErrors(prev => ({ ...prev, [field]: errorMsg }));
  };

  const handleEditChange = (field, value) => {
    setEditData(prev => ({ ...prev, [field]: value }));
    validateField(field, value);
  };

  // Multi-field verification block before API updates
  const isFormValid = () => {
    const freshErrors = {};
    
    Object.keys(editData).forEach(key => {
      const value = editData[key] || '';
      if (!value.toString().trim() && key !== 'logo_url') {
        freshErrors[key] = 'This field is required.';
      }
    });

    const finalErrors = { ...errors, ...freshErrors };
    setErrors(finalErrors);

    return !Object.values(finalErrors).some(err => err !== '');
  };

  const handleSaveEdit = async () => {
    if (!isFormValid()) return;

    try {
      setSaveLoading(true);
      await updateProject(selectedProject.id, editData);
      
      const updatedProjects = projects.map(p =>
        p.id === selectedProject.id ? { ...p, ...editData } : p
      );
      setProjects(updatedProjects);
      
      setSelectedProject(prev => ({ ...prev, ...editData }));
      setEditMode(false);
      setEditData({});
      setErrors({});
      toast.success('Project updated successfully!');
    } catch (err) {
      toast.error('Error updating project');
      // alert('Error updating project: ' + err.message);
    } finally {
      setSaveLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeleteLoading(true);
      await deleteProject(selectedProject.id);
      setProjects(projects.filter(p => p.id !== selectedProject.id));
      setSelectedProject(null);
      setDeleteConfirm(false);
    } catch (err) {
      toast.error('Error deleting project: ' + err.message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditData({});
    setErrors({});
  };

  if (loading) {
    return <div className="projects-container"><p className="loading">Loading projects...</p></div>;
  }

  if (error) {
    return (
      <div className="projects-container">
        <div className="error-message">
          <p>Error: {error}</p>
          {error.includes('Session expired') && <p style={{ marginTop: '10px', fontSize: '0.9rem' }}>Redirecting to login...</p>}
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="projects-container">
        <div className="no-projects">
          <p>No projects found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="projects-container">
      <div className="projects-header">
        <h1>Your Projects</h1>
        <p className="projects-count">Total Projects: {projects.length}</p>
      </div>

      <div className="projects-grid">
        {projects.map((project) => (
          <div key={project.id} className="project-card">
            <div className="project-header">
              <div className="project-logo">
                {project.logo_url ? (
                  <img 
                    src={getProjectLogo(project.logo_url)} 
                    alt={`${project.name} logo`} 
                    onError={(e) => {
                      e.target.onerror = null; 
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = `<div class="logo-placeholder">${project.name.charAt(0)}</div>`;
                    }}
                  />
                ) : (
                  <div className="logo-placeholder">{project.name.charAt(0)}</div>
                )}
              </div>
              <span className={`status-badge status-${project.status.toLowerCase()}`}>
                {project.status}
              </span>
            </div>

            <div className="project-content">
              <h3 className="project-name">{project.name}</h3>
              
              <div className="project-quick-info">
                <div className="quick-info-item">
                  <span className="info-label">Owner:</span>
                  <span className="info-value">{project.owner?.first_name} {project.owner?.last_name}</span>
                </div>

                <div className="quick-info-item">
                  <span className="info-label">Status:</span>
                  <span className={`active-status ${project.is_active ? 'active' : 'inactive'}`}>
                    {project.is_active ? '● Active' : '● Inactive'}
                  </span>
                </div>
              </div>

              {project.configs && project.configs.length > 0 && (
                <div className="quick-configs">
                  <span className="config-badge">{project.configs.length} Config{project.configs.length !== 1 ? 's' : ''}</span>
                </div>
              )}

              <button className="view-details-btn" onClick={() => setSelectedProject(project)}>
                View Details
              </button>
            </div>
          </div>
        ))}
      </div>

      {selectedProject && !deleteConfirm && (
        <div className="modal-overlay" onClick={() => setSelectedProject(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setSelectedProject(null)}>×</button>
            
            <div className="modal-header">
              <div className="modal-logo">
                {editMode ? (
                  <div className="modal-input-wrapper">
                    <input
                      type="text"
                      value={editData.logo_url || ''}
                      onChange={(e) => handleEditChange('logo_url', e.target.value)}
                      placeholder="Logo URL"
                      className={`edit-input-small ${errors.logo_url ? 'input-error' : ''}`}
                    />
                    {errors.logo_url && <span className="absolute-error">{errors.logo_url}</span>}
                  </div>
                ) : selectedProject.logo_url ? (
                  <img 
                    src={getProjectLogo(selectedProject.logo_url)} 
                    alt={selectedProject.name} 
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = 'none';
                      e.target.parentNode.innerHTML = `<div class="logo-placeholder">${selectedProject.name.charAt(0)}</div>`;
                    }}
                  />
                ) : (
                  <div className="logo-placeholder">{selectedProject.name.charAt(0)}</div>
                )}
              </div>
              <div className="modal-header-content">
                <h2>
                  {editMode ? (
                    <div className="modal-input-wrapper">
                      <input
                        type="text"
                        value={editData.name || ''}
                        onChange={(e) => handleEditChange('name', e.target.value)}
                        onKeyDown={(e) => handleKeyDown(e, 'name')}
                        className={`edit-input-title ${errors.name ? 'input-error' : ''}`}
                      />
                      {errors.name && <span className="absolute-error">{errors.name}</span>}
                    </div>
                  ) : (
                    selectedProject.name
                  )}
                </h2>
                <span className={`status-badge status-${selectedProject.status.toLowerCase()}`}>
                  {selectedProject.status}
                </span>
              </div>
            </div>

            <div className="modal-body">
              <div className="detail-section">
                <h4>Project Information</h4>
                <div className="detail-row">
                  <span className="detail-label">API Key:</span>
                  <span className="detail-value">{selectedProject.api_key || 'Not Generated'}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Owner:</span>
                  <span className="detail-value">{selectedProject.owner?.first_name} {selectedProject.owner?.last_name}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Email:</span>
                  <span className="detail-value">{selectedProject.owner?.email}</span>
                </div>

                {/* Sender Name Section */}
                <div className="detail-row">
                  <span className="detail-label">Sender Name:</span>
                  {editMode ? (
                    <div className="modal-input-wrapper">
                      <input
                        type="text"
                        value={editData.sender_name || ''}
                        onChange={(e) => handleEditChange('sender_name', e.target.value)}
                        className={`edit-input ${errors.sender_name ? 'input-error' : ''}`}
                      />
                      {errors.sender_name && <span className="absolute-error">{errors.sender_name}</span>}
                    </div>
                  ) : (
                    <span className="detail-value">{selectedProject.sender_name || 'Not Available'}</span>
                  )}
                </div>

                {/* Sender Email Section */}
                <div className="detail-row">
                  <span className="detail-label">Sender Email:</span>
                  {editMode ? (
                    <div className="modal-input-wrapper">
                      <input
                        type="email"
                        value={editData.sender_email || ''}
                        onChange={(e) => handleEditChange('sender_email', e.target.value)}
                        className={`edit-input ${errors.sender_email ? 'input-error' : ''}`}
                      />
                      {errors.sender_email && <span className="absolute-error">{errors.sender_email}</span>}
                    </div>
                  ) : (
                    <span className="detail-value">{selectedProject.sender_email || 'Not Available'}</span>
                  )}
                </div>

                <div className="detail-row">
                  <span className="detail-label">Status:</span>
                  <span className={`active-status ${selectedProject.is_active ? 'active' : 'inactive'}`}>
                    {selectedProject.is_active ? '● Active' : '● Inactive'}
                  </span>
                </div>
              </div>

              <div className="detail-section">
                <h4>URLs</h4>
                <div className="detail-row">
                  <span className="detail-label">Base URL:</span>
                  {editMode ? (
                    <div className="modal-input-wrapper">
                      <input
                        type="text"
                        value={editData.base_url || ''}
                        onChange={(e) => handleEditChange('base_url', e.target.value)}
                        className={`edit-input ${errors.base_url ? 'input-error' : ''}`}
                      />
                      {errors.base_url && <span className="absolute-error">{errors.base_url}</span>}
                    </div>
                  ) : (
                    <a href={selectedProject.base_url} target="_blank" rel="noopener noreferrer" className="detail-link">
                      {selectedProject.base_url}
                    </a>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h4>Dates</h4>
                <div className="detail-row">
                  <span className="detail-label">Requested:</span>
                  <span className="detail-value">{new Date(selectedProject.request_date).toLocaleString()}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Approved:</span>
                  <span className="detail-value">{selectedProject.approved_date ? new Date(selectedProject.approved_date).toLocaleString() : 'Pending'}</span>
                </div>
              </div>

              {selectedProject.configs && selectedProject.configs.length > 0 && (
                <div className="detail-section">
                  <h4>Templates ({selectedProject.configs.length})</h4>
                  <div className="configs-grid">
                    {selectedProject.configs.map((config) => (
                      <div key={config.id} className="config-card">
                        <div className="config-color" style={{ backgroundColor: config.primary_color }}></div>
                        <p className="config-template">{config.template_name}</p>
                        <p className="config-design">{config.design_name}</p>
                        <span className="config-channel">{config.channel_type}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              {editMode ? (
                <div className="action-buttons">
                  <button 
                    className="btn btn-primary" 
                    onClick={handleSaveEdit} 
                    disabled={saveLoading}
                  >
                    {saveLoading ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button 
                    className="btn btn-secondary" 
                    onClick={handleCancel}
                    disabled={saveLoading}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="action-buttons">
                  <button className="btn btn-primary" onClick={handleEditClick}>
                    Edit Project
                  </button>
                  <button className="btn btn-danger" onClick={handleDeleteClick}>
                    Delete Project
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <h3>Delete Project</h3>
            <p>Are you sure you want to delete the project "<strong>{selectedProject.name}</strong>"?</p>
            <p className="warning-text">This action will deactivate the project and it cannot be undone.</p>
            <div className="action-buttons">
              <button 
                className="btn btn-danger" 
                onClick={handleConfirmDelete}
                disabled={deleteLoading}
              >
                {deleteLoading ? 'Deleting...' : 'Yes, Delete'}
              </button>
              <button 
                className="btn btn-secondary" 
                onClick={() => setDeleteConfirm(false)}
                disabled={deleteLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Projects;


// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import { getProjects, updateProject, deleteProject} from '../../../services/projectService';
// import './Projects.css';

// // Fallback host constant if the backend does not return an absolute URI string natively
// const BACKEND_URL = 'http://127.0.0.1:8000';

// function Projects() {
//   const [projects, setProjects] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [selectedProject, setSelectedProject] = useState(null);
//   const [editMode, setEditMode] = useState(false);
//   const [editData, setEditData] = useState({});
//   const [saveLoading, setSaveLoading] = useState(false);
//   const [deleteConfirm, setDeleteConfirm] = useState(false);
//   const [deleteLoading, setDeleteLoading] = useState(false);
//   const navigate = useNavigate();

//   useEffect(() => {
//     fetchProjects();
//   }, [navigate]);

//   const fetchProjects = async () => {
//     try {
//       setLoading(true);
//       setError(null);
//       const data = await getProjects();
//       setProjects(data);
//     } catch (err) {
//       const errorMsg = err.message || 'Failed to fetch projects';
//       setError(errorMsg);
//       console.error('Error:', err);
      
//       if (err.message.includes('Session expired') || err.message.includes('Authorization token not found')) {
//         setTimeout(() => {
//           navigate('/');
//         }, 2000);
//       }
//     } finally {
//       setLoading(false);
//     }
//   };

//   // Helper function to dynamically resolve full logo paths safely
//   const getProjectLogo = (logoUrl) => {
//     if (!logoUrl) return null;
//     if (logoUrl.startsWith('http://') || logoUrl.startsWith('https://')) {
//       return logoUrl;
//     }
//     if (logoUrl.startsWith('/')) {
//       return `${BACKEND_URL}${logoUrl}`;
//     }
//     return `${BACKEND_URL}/media/${logoUrl}`;
//   };

//   const handleEditClick = () => {
//     setEditMode(true);
//     setEditData({
//       name: selectedProject.name,
//       base_url: selectedProject.base_url,
//       logo_url: selectedProject.logo_url,
//     });
//   };

//   const handleEditChange = (field, value) => {
//     setEditData(prev => ({
//       ...prev,
//       [field]: value
//     }));
//   };

//   const handleSaveEdit = async () => {
//     try {
//       setSaveLoading(true);
//       await updateProject(selectedProject.id, editData);
      
//       // Update local state
//       const updatedProjects = projects.map(p =>
//         p.id === selectedProject.id ? { ...p, ...editData } : p
//       );
//       setProjects(updatedProjects);
      
//       // Update selected project
//       setSelectedProject(prev => ({ ...prev, ...editData }));
//       setEditMode(false);
//       setEditData({});
//     } catch (err) {
//       alert('Error updating project: ' + err.message);
//     } finally {
//       setSaveLoading(false);
//     }
//   };

//   const handleDeleteClick = () => {
//     setDeleteConfirm(true);
//   };

//   const handleConfirmDelete = async () => {
//     try {
//       setDeleteLoading(true);
//       await deleteProject(selectedProject.id);
      
//       // Remove from local state
//       setProjects(projects.filter(p => p.id !== selectedProject.id));
      
//       setSelectedProject(null);
//       setDeleteConfirm(false);
//     } catch (err) {
//       alert('Error deleting project: ' + err.message);
//     } finally {
//       setDeleteLoading(false);
//     }
//   };

//   const handleCancel = () => {
//     setEditMode(false);
//     setEditData({});
//   };

//   if (loading) {
//     return <div className="projects-container"><p className="loading">Loading projects...</p></div>;
//   }

//   if (error) {
//     return (
//       <div className="projects-container">
//         <div className="error-message">
//           <p>Error: {error}</p>
//           {error.includes('Session expired') && <p style={{ marginTop: '10px', fontSize: '0.9rem' }}>Redirecting to login...</p>}
//         </div>
//       </div>
//     );
//   }

//   if (projects.length === 0) {
//     return (
//       <div className="projects-container">
//         <div className="no-projects">
//           <p>No projects found</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="projects-container">
//       <div className="projects-header">
//         <h1>Your Projects</h1>
//         <p className="projects-count">Total Projects: {projects.length}</p>
//       </div>

//       <div className="projects-grid">
//         {projects.map((project) => (
//           <div key={project.id} className="project-card">
//             <div className="project-header">
//               <div className="project-logo">
//                 {project.logo_url ? (
//                   <img 
//                     src={getProjectLogo(project.logo_url)} 
//                     alt={`${project.name} logo`} 
//                     onError={(e) => {
//                       e.target.onerror = null; 
//                       e.target.style.display = 'none';
//                       e.target.parentNode.innerHTML = `<div class="logo-placeholder">${project.name.charAt(0)}</div>`;
//                     }}
//                   />
//                 ) : (
//                   <div className="logo-placeholder">{project.name.charAt(0)}</div>
//                 )}
//               </div>
//               <span className={`status-badge status-${project.status.toLowerCase()}`}>
//                 {project.status}
//               </span>
//             </div>

//             <div className="project-content">
//               <h3 className="project-name">{project.name}</h3>
              
//               <div className="project-quick-info">
//                 <div className="quick-info-item">
//                   <span className="info-label">Owner:</span>
//                   <span className="info-value">{project.owner?.first_name} {project.owner?.last_name}</span>
//                 </div>

//                 <div className="quick-info-item">
//                   <span className="info-label">Status:</span>
//                   <span className={`active-status ${project.is_active ? 'active' : 'inactive'}`}>
//                     {project.is_active ? '● Active' : '● Inactive'}
//                   </span>
//                 </div>
//               </div>

//               {project.configs && project.configs.length > 0 && (
//                 <div className="quick-configs">
//                   <span className="config-badge">{project.configs.length} Template{project.configs.length !== 1 ? 's' : ''}</span>
//                 </div>
//               )}

//               <button className="view-details-btn" onClick={() => setSelectedProject(project)}>
//                 View Details
//               </button>
//             </div>
//           </div>
//         ))}
//       </div>

//       {selectedProject && !deleteConfirm && (
//         <div className="modal-overlay" onClick={() => setSelectedProject(null)}>
//           <div className="modal-content" onClick={(e) => e.stopPropagation()}>
//             <button className="modal-close" onClick={() => setSelectedProject(null)}>×</button>
            
//             <div className="modal-header">
//               <div className="modal-logo">
//                 {editMode ? (
//                   <input
//                     type="text"
//                     value={editData.logo_url || ''}
//                     onChange={(e) => handleEditChange('logo_url', e.target.value)}
//                     placeholder="Logo URL"
//                     className="edit-input-small"
//                   />
//                 ) : selectedProject.logo_url ? (
//                   <img 
//                     src={getProjectLogo(selectedProject.logo_url)} 
//                     alt={selectedProject.name} 
//                     onError={(e) => {
//                       e.target.onerror = null;
//                       e.target.style.display = 'none';
//                       e.target.parentNode.innerHTML = `<div class="logo-placeholder">${selectedProject.name.charAt(0)}</div>`;
//                     }}
//                   />
//                 ) : (
//                   <div className="logo-placeholder">{selectedProject.name.charAt(0)}</div>
//                 )}
//               </div>
//               <div className="modal-header-content">
//                 <h2>
//                   {editMode ? (
//                     <input
//                       type="text"
//                       value={editData.name || ''}
//                       onChange={(e) => handleEditChange('name', e.target.value)}
//                       className="edit-input-title"
//                     />
//                   ) : (
//                     selectedProject.name
//                   )}
//                 </h2>
//                 <span className={`status-badge status-${selectedProject.status.toLowerCase()}`}>
//                   {selectedProject.status}
//                 </span>
//               </div>
//             </div>

//             <div className="modal-body">
//               <div className="detail-section">
//                 <h4>Project Information</h4>
//                 <div className="detail-row">
//                   <span className="detail-label">API Key:</span>
//                   <span className="detail-value">{selectedProject.api_key || 'Not Generated'}</span>
//                 </div>
//                 <div className="detail-row">
//                   <span className="detail-label">Owner:</span>
//                   <span className="detail-value">{selectedProject.owner?.first_name} {selectedProject.owner?.last_name}</span>
//                 </div>
//                 <div className="detail-row">
//                   <span className="detail-label">Email:</span>
//                   <span className="detail-value">{selectedProject.owner?.email}</span>
//                 </div>
//                   <div className="detail-row">
//     <span className="detail-label">Sender Name:</span>
//     <span className="detail-value">
//       {selectedProject.sender_name || 'Not Available'}
//     </span>
//   </div>

//   {/* Sender Email */}
//   <div className="detail-row">
//     <span className="detail-label">Sender Email:</span>
//     <span className="detail-value">
//       {selectedProject.sender_email || 'Not Available'}
//     </span>
//   </div>
//                 <div className="detail-row">
//                   <span className="detail-label">Status:</span>
//                   <span className={`active-status ${selectedProject.is_active ? 'active' : 'inactive'}`}>
//                     {selectedProject.is_active ? '● Active' : '● Inactive'}
//                   </span>
//                 </div>
//               </div>

//               <div className="detail-section">
//                 <h4>URLs</h4>
//                 <div className="detail-row">
//                   <span className="detail-label">Base URL:</span>
//                   {editMode ? (
//                     <input
//                       type="text"
//                       value={editData.base_url || ''}
//                       onChange={(e) => handleEditChange('base_url', e.target.value)}
//                       className="edit-input"
//                     />
//                   ) : (
//                     <a href={selectedProject.base_url} target="_blank" rel="noopener noreferrer" className="detail-link">
//                       {selectedProject.base_url}
//                     </a>
//                   )}
//                 </div>
//               </div>

//               <div className="detail-section">
//                 <h4>Dates</h4>
//                 <div className="detail-row">
//                   <span className="detail-label">Requested:</span>
//                   <span className="detail-value">{new Date(selectedProject.request_date).toLocaleString()}</span>
//                 </div>
//                 <div className="detail-row">
//                   <span className="detail-label">Approved:</span>
//                   <span className="detail-value">{selectedProject.approved_date ? new Date(selectedProject.approved_date).toLocaleString() : 'Pending'}</span>
//                 </div>
//               </div>

//               {selectedProject.configs && selectedProject.configs.length > 0 && (
//                 <div className="detail-section">
//                   <h4>Templates ({selectedProject.configs.length})</h4>
//                   <div className="configs-grid">
//                     {selectedProject.configs.map((config) => (
//                       <div key={config.id} className="config-card">
//                         <div className="config-color" style={{ backgroundColor: config.primary_color }}></div>
//                         <p className="config-template">{config.template_name}</p>
//                         <p className="config-design">{config.design_name}</p>
//                         <span className="config-channel">{config.channel_type}</span>
//                       </div>
//                     ))}
//                   </div>
//                 </div>
//               )}
//             </div>

//             <div className="modal-footer">
//               {editMode ? (
//                 <div className="action-buttons">
//                   <button 
//                     className="btn btn-primary" 
//                     onClick={handleSaveEdit} 
//                     disabled={saveLoading}
//                   >
//                     {saveLoading ? 'Saving...' : 'Save Changes'}
//                   </button>
//                   <button 
//                     className="btn btn-secondary" 
//                     onClick={handleCancel}
//                     disabled={saveLoading}
//                   >
//                     Cancel
//                   </button>
//                 </div>
//               ) : (
//                 <div className="action-buttons">
//                   <button className="btn btn-primary" onClick={handleEditClick}>
//                     Edit Project
//                   </button>
//                   <button className="btn btn-danger" onClick={handleDeleteClick}>
//                     Delete Project
//                   </button>
//                 </div>
//               )}
//             </div>
//           </div>
//         </div>
//       )}

//       {deleteConfirm && (
//         <div className="modal-overlay" onClick={() => setDeleteConfirm(false)}>
//           <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
//             <h3>Delete Project</h3>
//             <p>Are you sure you want to delete the project "<strong>{selectedProject.name}</strong>"?</p>
//             <p className="warning-text">This action will deactivate the project and it cannot be undone.</p>
//             <div className="action-buttons">
//               <button 
//                 className="btn btn-danger" 
//                 onClick={handleConfirmDelete}
//                 disabled={deleteLoading}
//               >
//                 {deleteLoading ? 'Deleting...' : 'Yes, Delete'}
//               </button>
//               <button 
//                 className="btn btn-secondary" 
//                 onClick={() => setDeleteConfirm(false)}
//                 disabled={deleteLoading}
//               >
//                 Cancel
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default Projects;